/**
 * Support API Client
 *
 * Client-side API functions for support ticket system.
 * Uses services/api.js for automatic token refresh and session management.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Security: Fixed January 2026 - Added Bearer token auth and token refresh
 * Location: apps/web/src/services/supportApi.js
 */

import apiRequest from './api';

/**
 * Support Queue API
 * Handles support requests/tickets
 */
export const supportQueueApi = {
  /**
   * Get all support requests (queue)
   */
  async getQueue(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) {params.append('status', filters.status);}
    if (filters.priority) {params.append('priority', filters.priority);}
    if (filters.assigned_to) {params.append('assigned_to', filters.assigned_to);}
    if (filters.limit) {params.append('limit', filters.limit);}
    if (filters.offset) {params.append('offset', filters.offset);}

    const response = await apiRequest(`/api/v1/support/queue?${params}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get queue statistics
   */
  async getStats() {
    const response = await apiRequest('/api/v1/support/queue/stats', {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get single support request by ID
   */
  async getById(id) {
    const response = await apiRequest(`/api/v1/support/queue/${id}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Create new support request
   */
  async create(requestData) {
    const response = await apiRequest('/api/v1/support/queue', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
    return response.data;
  },

  /**
   * Update support request
   */
  async update(id, updates) {
    const response = await apiRequest(`/api/v1/support/queue/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    return response.data;
  },

  /**
   * Assign support request
   */
  async assign(id, userId) {
    return await this.update(id, { assigned_to: userId });
  },

  /**
   * Change support request status
   */
  async updateStatus(id, status) {
    return await this.update(id, { status });
  },

  /**
   * Add note to support request
   */
  async addNote(id, noteData) {
    const response = await apiRequest(`/api/v1/support/queue/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify(noteData)
    });
    return response.data;
  },

  /**
   * Get activity timeline for support request
   */
  async getTimeline(id) {
    const response = await apiRequest(`/api/v1/support/queue/${id}/timeline`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Search users (for ticket context)
   */
  async searchUsers(query) {
    const params = new URLSearchParams({ search: query, limit: 20 });
    const response = await apiRequest(`/api/v1/support/search/users?${params}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Search bookings (for ticket context)
   */
  async searchBookings(query) {
    const params = new URLSearchParams({ search: query, limit: 20 });
    const response = await apiRequest(`/api/v1/support/search/bookings?${params}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Bulk assign tickets to a user
   */
  async bulkAssign(ticketIds, assignedToUserId) {
    const response = await apiRequest('/api/v1/support/queue/bulk-assign', {
      method: 'POST',
      body: JSON.stringify({
        ticket_ids: ticketIds,
        assigned_to_user_id: assignedToUserId,
      })
    });
    return response.data;
  },

  /**
   * Bulk update status of tickets
   */
  async bulkUpdateStatus(ticketIds, status) {
    const response = await apiRequest('/api/v1/support/queue/bulk-update-status', {
      method: 'POST',
      body: JSON.stringify({
        ticket_ids: ticketIds,
        status,
      })
    });
    return response.data;
  },

  /**
   * Bulk close resolved tickets
   */
  async bulkClose(ticketIds) {
    const response = await apiRequest('/api/v1/support/queue/bulk-close', {
      method: 'POST',
      body: JSON.stringify({
        ticket_ids: ticketIds,
      })
    });
    return response.data;
  },
};

/**
 * Canned Response API
 * Handles support response templates
 */
export const cannedResponseApi = {
  /**
   * Get all canned responses
   */
  async getAll(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) {params.append('category', filters.category);}
    if (filters.is_active !== undefined) {params.append('is_active', filters.is_active);}
    if (filters.search) {params.append('search', filters.search);}
    if (filters.limit) {params.append('limit', filters.limit);}
    if (filters.offset) {params.append('offset', filters.offset);}

    const response = await apiRequest(`/api/v1/support/canned-responses?${params}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get available categories
   */
  async getCategories() {
    const response = await apiRequest('/api/v1/support/canned-responses/categories', {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get canned response by ID
   */
  async getById(id) {
    const response = await apiRequest(`/api/v1/support/canned-responses/${id}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Create canned response
   */
  async create(responseData) {
    const response = await apiRequest('/api/v1/support/canned-responses', {
      method: 'POST',
      body: JSON.stringify(responseData)
    });
    return response.data;
  },

  /**
   * Update canned response
   */
  async update(id, updates) {
    const response = await apiRequest(`/api/v1/support/canned-responses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    return response.data;
  },

  /**
   * Delete canned response
   */
  async delete(id) {
    const response = await apiRequest(`/api/v1/support/canned-responses/${id}`, {
      method: 'DELETE'
    });
    return response;
  },

  /**
   * Search canned responses
   */
  async search(query, limit = 20) {
    const response = await apiRequest('/api/v1/support/canned-responses/search', {
      method: 'POST',
      body: JSON.stringify({ query, limit })
    });
    return response.data;
  },

  /**
   * Use canned response (track usage)
   */
  async use(id, supportRequestId = null) {
    const response = await apiRequest(`/api/v1/support/canned-responses/${id}/use`, {
      method: 'POST',
      body: JSON.stringify({ support_request_id: supportRequestId })
    });
    return response.data;
  },

  /**
   * Get usage statistics
   */
  async getStats(options = {}) {
    const params = new URLSearchParams();
    if (options.limit) {params.append('limit', options.limit);}
    if (options.since) {params.append('since', options.since);}

    const response = await apiRequest(`/api/v1/support/canned-responses/stats?${params}`, {
      method: 'GET'
    });
    return response.data;
  },
};

/**
 * Support Analytics API
 * Endpoints for analytics and reporting
 */
export const supportAnalyticsApi = {
  /**
   * Get overview analytics
   */
  async getOverview({ period = '30d' } = {}) {
    const response = await apiRequest(`/api/v1/support/analytics/overview?period=${period}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get daily metrics time series
   */
  async getDailyMetrics({ period = '30d', priority = null, request_type = null } = {}) {
    const params = new URLSearchParams({ period });
    if (priority) {params.append('priority', priority);}
    if (request_type) {params.append('request_type', request_type);}

    const response = await apiRequest(`/api/v1/support/analytics/daily?${params}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get staff performance metrics
   */
  async getStaffMetrics() {
    const response = await apiRequest('/api/v1/support/analytics/staff', {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get request type breakdown
   */
  async getTypeBreakdown() {
    const response = await apiRequest('/api/v1/support/analytics/types', {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get hourly trends (heatmap data)
   */
  async getHourlyTrends() {
    const response = await apiRequest('/api/v1/support/analytics/hourly', {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Get resolution time distribution
   */
  async getResolutionTimeDistribution({ period = '30d' } = {}) {
    const response = await apiRequest(`/api/v1/support/analytics/resolution-time?period=${period}`, {
      method: 'GET'
    });
    return response.data;
  },

  /**
   * Manually refresh materialized views
   */
  async refreshMaterializedViews() {
    const response = await apiRequest('/api/v1/support/analytics/refresh', {
      method: 'POST'
    });
    return response.data;
  },

  /**
   * Get last refresh timestamp
   */
  async getLastRefresh() {
    const response = await apiRequest('/api/v1/support/analytics/last-refresh', {
      method: 'GET'
    });
    return response.data;
  },
};
